export class Curve {
    xs;
    ys;
    ms; // tangent (slope) at each point
    constructor(points) {
        // Defensive: sort + dedupe so a malformed config can't produce a non-monotone x axis.
        const pts = [...points].sort((a, b) => a.x - b.x).filter((p, i, a) => i === 0 || p.x !== a[i - 1].x);
        const n = Math.max(2, pts.length);
        this.xs = new Float64Array(n);
        this.ys = new Float64Array(n);
        this.ms = new Float64Array(n);
        if (pts.length < 2) {
            // Degenerate: flat line at the single (or default) value.
            const y = pts.length === 1 ? pts[0].y : 0;
            this.xs[0] = 0;
            this.ys[0] = y;
            this.xs[1] = 1;
            this.ys[1] = y;
        }
        else {
            for (let i = 0; i < pts.length; i++) {
                this.xs[i] = pts[i].x;
                this.ys[i] = pts[i].y;
            }
        }
        this.computeTangents();
    }
    /** Fritsch–Carlson monotone tangents: prevents overshoot between control points. */
    computeTangents() {
        const { xs, ys, ms } = this;
        const n = xs.length;
        const d = new Float64Array(n - 1); // secant slopes
        for (let i = 0; i < n - 1; i++)
            d[i] = (ys[i + 1] - ys[i]) / (xs[i + 1] - xs[i]);
        ms[0] = d[0];
        ms[n - 1] = d[n - 2];
        for (let i = 1; i < n - 1; i++) {
            if (d[i - 1] * d[i] <= 0)
                ms[i] = 0; // local extremum → flat tangent (no overshoot)
            else
                ms[i] = (d[i - 1] + d[i]) / 2;
        }
        // Clamp tangents to keep the interpolant monotone (Fritsch–Carlson).
        for (let i = 0; i < n - 1; i++) {
            if (d[i] === 0) {
                ms[i] = 0;
                ms[i + 1] = 0;
                continue;
            }
            const a = ms[i] / d[i], b = ms[i + 1] / d[i];
            const s = a * a + b * b;
            if (s > 9) {
                const tau = 3 / Math.sqrt(s);
                ms[i] = tau * a * d[i];
                ms[i + 1] = tau * b * d[i];
            }
        }
    }
    /** Evaluate the curve at x (clamped to the control-point domain). */
    eval(x) {
        const { xs, ys, ms } = this;
        const n = xs.length;
        if (x <= xs[0])
            return ys[0];
        if (x >= xs[n - 1])
            return ys[n - 1];
        // Binary search for the segment [xs[i], xs[i+1]] containing x.
        let lo = 0, hi = n - 1;
        while (hi - lo > 1) {
            const mid = (lo + hi) >> 1;
            if (xs[mid] <= x)
                lo = mid;
            else
                hi = mid;
        }
        const h = xs[lo + 1] - xs[lo];
        const t = (x - xs[lo]) / h;
        const t2 = t * t, t3 = t2 * t;
        // Cubic Hermite basis.
        const h00 = 2 * t3 - 3 * t2 + 1;
        const h10 = t3 - 2 * t2 + t;
        const h01 = -2 * t3 + 3 * t2;
        const h11 = t3 - t2;
        return h00 * ys[lo] + h10 * h * ms[lo] + h01 * ys[lo + 1] + h11 * h * ms[lo + 1];
    }
}
/**
 * Default elevation curve, expressed as (noise 0..1 → voxels relative to sea level). Reproduces the
 * old ocean-shelf / beach / plains / mountain shape but with a SOFT sea→beach transition (the old
 * hardcoded curve had a hard step at the waterline that z-fought the water shader). Scaled by
 * mountainHeight/seaDepth/beachWidth at eval time, so the shape is authored once and the config
 * knobs stretch it. Height OFFSET so it's independent of sea level.
 */
// NOTE on x placement: the continental noise is FBm, whose values cluster near the middle — u rarely
// exceeds ~0.85. So the mountain ramp + plateau MUST live in the reachable band (peaks by ~u 0.82),
// or mountains/snow essentially never appear. The ocean descent is spread across the whole u<0.5 tail
// so the seabed is a gradual slope, not a cliff off the beach.
export const DEFAULT_LANDFORM_CURVE = [
    { x: 0.00, y: -1.00 }, // deepest ocean floor (× seaDepth)
    { x: 0.10, y: -1.00 }, // PLATEAU: flat deep sea floor across the bottom of the range
    { x: 0.24, y: -0.50 }, // gradual rise…
    { x: 0.34, y: -0.15 },
    { x: 0.40, y: 0.00 }, // waterline moved LEFT → ~30% sea (much less), more land
    { x: 0.45, y: 0.03 }, // gentle sand beach
    { x: 0.53, y: 0.10 }, // grass plains
    { x: 0.63, y: 0.24 }, // hills — big mountainous band begins low so mountains are common
    { x: 0.73, y: 0.46 }, // low mountains (climbable grade, rugged via detail)
    { x: 0.83, y: 0.72 }, // mountains (snow line ~ here)
    { x: 0.92, y: 0.93 }, // upper slopes
    { x: 1.00, y: 1.00 }, // plateau: flat snowy peak tops
];
//# sourceMappingURL=Curve.js.map